import {
  Checkbox,
  Dropdown,
  H4,
  Input,
  Label,
  Radio,
  Stack,
  Grid
} from "@adfenix/alexandria";
import { ProgramService } from "@adfenix/types";
import { Controller, useFormContext } from "react-hook-form";
import { useNestedFormData } from "../useNestedFormData";
import { useDisabledFields } from "../useDisabledFields";
import { numberValidator } from "utils/react-hook-form";
import { useCheckPermission } from "hooks/usePermissions";
import { useOfficeAndHq } from "hooks/useOfficeAndHq";
import { useTranslation } from "react-i18next";
import { formMaxWidth } from ".";

interface Props {
  bundleGroupIndex: number;
  bundleIndex: number;
}

export const PaymentSettings = ({ bundleIndex, bundleGroupIndex }: Props) => {
  const { t } = useTranslation();

  const { hq } = useOfficeAndHq();
  const { register, control, setValue } =
    useFormContext<ProgramService.ProgramModel>();
  const disabledFields = useDisabledFields();

  const canEditBudget = useCheckPermission({
    scope: "Budget",
    action: "Edit"
  });

  const { bundle } = useNestedFormData({
    bundleIndex,
    bundleGroupIndex
  });

  const creditCardPayer: ProgramService.PaidBy[] = [
    "Agent",
    "Hq",
    "Office",
    "Seller"
  ];

  const getPayers = (): ProgramService.PaidBy[] => {
    if (bundle.paymentMethod === "Invoice") {
      return creditCardPayer.filter(
        payer => payer !== "Agent" && payer !== "Seller"
      );
    } else if (bundle.paymentMethod === "Contract") {
      return creditCardPayer.filter(payer => payer === "Seller");
    }

    return creditCardPayer;
  };

  const handlePaymentMethod = (
    role: ProgramService.PaidBy,
    paymentMethod: ProgramService.PaymentMethod
  ) => {
    setValue(
      `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.paidBy`,
      role
    );

    setValue(
      `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.paymentMethod`,
      paymentMethod
    );
  };

  return (
    <>
      <Stack spacing="l" style={{ maxWidth: formMaxWidth }}>
        <H4>{t("programBuilder.program.details.payment.header")}</H4>
        <Grid columns={["1fr", "1fr 1fr 1fr"]} gap="l">
          <Controller
            control={control}
            name={`bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.paidBy`}
            render={({ field: { ref, value, ...rest } }) => (
              <Dropdown
                items={getPayers()}
                {...rest}
                label={t(
                  "programBuilder.program.details.payment.settings.label"
                )}
                placeholder={t(
                  "programBuilder.program.details.payment.settings.placeholder"
                )}
                value={value}
                disabled={disabledFields("bundleGroups.bundles.paidBy")}
              />
            )}
          />
          <Stack spacing="xs">
            <Label>
              {t(
                "programBuilder.program.details.payment.settings.method.header"
              )}
            </Label>
            <Radio
              {...register(
                `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.paymentMethod`
              )}
              value="CreditCard"
              id="CreditCard"
              readOnly={disabledFields("bundleGroups.bundles.paymentMethod")}
              label={t("programBuilder.program.details.payment.creditCard")}
              onChange={() => handlePaymentMethod("Hq", "CreditCard")}
            />

            <Radio
              {...register(
                `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.paymentMethod`
              )}
              value="Invoice"
              id="Invoice"
              readOnly={disabledFields("bundleGroups.bundles.paymentMethod")}
              label={t("programBuilder.program.details.payment.invoice")}
              onChange={() => handlePaymentMethod("Hq", "Invoice")}
            />

            <Radio
              {...register(
                `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.paymentMethod`
              )}
              value="Contract"
              id="Contract"
              readOnly={disabledFields("bundleGroups.bundles.paymentMethod")}
              label={t("programBuilder.program.details.payment.contract")}
              onChange={() => handlePaymentMethod("Seller", "Contract")}
            />
          </Stack>
          <Stack spacing="xs">
            <Label>
              {t("programBuilder.program.details.payment.settings.header")}
            </Label>

            <Checkbox
              {...register(
                `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.hidePrice`
              )}
              label={t(
                "programBuilder.program.details.payment.checkbox.hidePrice"
              )}
              readOnly={disabledFields("bundleGroups.bundles.hidePrice")}
            />
            <Checkbox
              {...register(
                `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.hidePaidBy`
              )}
              label={t(
                "programBuilder.program.details.payment.checkbox.hidePaidBy"
              )}
              readOnly={disabledFields("bundleGroups.bundles.hidePaidBy")}
            />
            <Checkbox
              {...register(
                `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.requiresPermission`
              )}
              label={t(
                "programBuilder.program.details.payment.checkbox.requiresPermission"
              )}
              readOnly={disabledFields(
                "bundleGroups.bundles.requiresPermission"
              )}
            />
          </Stack>
        </Grid>
        <Grid columns={["1fr", "1fr 1fr"]} gap="l">
          <Input
            label={`${t(
              "programBuilder.program.details.payment.bundle.price.label"
            )} (${hq?.currency ?? "SEK"})`}
            {...register(
              `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.price.amount`,
              {
                valueAsNumber: true,
                validate: numberValidator("Price"),
                min: {
                  value: 0,
                  message: t(
                    "programBuilder.program.details.payment.bundle.price.validation.message"
                  )
                },
                required: t(
                  "programBuilder.program.details.payment.bundle.price.required.message"
                )
              }
            )}
          />

          {canEditBudget && (
            <Input
              label={`${t(
                "programBuilder.program.details.payment.bundle.totalBudget.label"
              )} (${hq?.currency ?? "SEK"})`}
              {...register(
                `bundleGroups.${bundleGroupIndex}.bundles.${bundleIndex}.totalBudget`,
                {
                  required: t(
                    "programBuilder.program.details.payment.bundle.totalBudget.required.message"
                  ),
                  valueAsNumber: true,
                  validate: numberValidator("Total budget"),
                  min: {
                    value: 0.1,
                    message: t(
                      "programBuilder.program.details.payment.bundle.totalBudget.validation.message"
                    )
                  }
                }
              )}
            />
          )}
        </Grid>
      </Stack>
    </>
  );
};
